import React, { useState } from "react";
import { Modal, ModalHeader, Image, Spinner } from "react-bootstrap";
import GuidedSetupBusinessInfo from "../guided_setup_businessinfo/guided_setup_businessinfo";
import TestNoura from "../../test_noura/test_noura";
import GuidedSetupCustNoura from "../guided_setup_cust_noura/guided_setup_cust_noura";
import authService from "../../../services/authService";
import { useUser } from "../../../context/UserContext";
import { useGuidedSetupBusinessContext } from "../../../context/GuidedSetupBusinessContext";

const steps = [
  { id: 1, title: "Add Business Info", subTitle: "Step 1" },
  { id: 2, title: "Customize Nouraa", subTitle: "Step 2" },
  { id: 3, title: "Test Nouraa", subTitle: "Step 3" },
];

const GuidedSetupDetails = ({ show }) => {
  const { token } = useUser();
  const [isCompleted, setIsCompleted] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [, setError] = useState(null);
  const { guidedSetupBusinessData } = useGuidedSetupBusinessContext();

  const handleStepClick = (stepId) => {
    if (stepId <= currentStep || completedSteps.includes(stepId)) {
      setCurrentStep(stepId);
      setCompletedSteps((prev) => [
        ...new Set([
          ...prev,
          ...Array.from({ length: stepId }, (_, i) => i + 1),
        ]),
      ]);
    }
  };

  const handleNext = async () => {
    if (currentStep < steps.length) {
      setCompletedSteps((prev) => [...new Set([...prev, currentStep])]); // Mark previous step as done
      setCurrentStep(currentStep + 1);
    }

    if (currentStep === 1) {
      try {
        const businessData = {
          name: guidedSetupBusinessData.name || "",
          website: guidedSetupBusinessData.website || "",
          address: guidedSetupBusinessData.address || "",
          country: guidedSetupBusinessData.country || "",
          bussinessOverview: guidedSetupBusinessData.overview || "",
          coreServices: guidedSetupBusinessData.coreServices || [],
          bussinessHours: guidedSetupBusinessData.businessHours.map(
            ({ day, from, to, open }) => ({
              day,
              hours: open ? `${from} - ${to}` : "",
            })
          ),
          direction: guidedSetupBusinessData.direction || "",
          timezone: guidedSetupBusinessData.timezone || "",
        };

        await authService.postBusinessInfo(token, businessData);
      } catch (error) {
        console.log("Error:", error);
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleLaunch = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Call the API to mark setup as complete
      await authService.markSetupComplete(token);

      // Only proceed if API call succeeds
      setCurrentStep(0);
      setIsCompleted(true);
    } catch (err) {
      setError(err.message || "Failed to complete setup");
      console.error("Setup completion error:", err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="modal-container-setup">
      <Modal show={show} centered size="xl">
        <ModalHeader
          className="p-3"
          style={{
            backgroundColor: "#FAFAFA",
            borderBottom: "1px solid #E9EAEB",
            position: "sticky", // Make header fixed
            top: 0,
            zIndex: 1020,
          }}
        >
          <p className="fw-bold fs-five fw-medium black-color mb-0">
            Guided Set-up
          </p>
        </ModalHeader>
        <Modal.Body className="p-4">
          {currentStep !== 0 && (
            <div
              className="stepper-tabs gap-1 flex-lg-row flex-column"
              style={{
                position: "sticky", // Make stepper tabs fixed
                top: "56px", // Adjust based on header height
                zIndex: 1010,
                backgroundColor: "#ffffff",
                paddingBottom: "1rem",
              }}
            >
              {steps.map((step) => (
                <div
                  key={step.id}
                  className={`stepper-tab d-flex flex-row ${
                    currentStep === step.id ? "active" : ""
                  } ${completedSteps.includes(step.id) ? "completed" : ""}`}
                  onClick={() => handleStepClick(step.id)}
                  style={{
                    cursor: "pointer",
                    gap: "1rem",
                  }}
                  id={`step-container-${step.id}`} // Unique ID for width calculation
                >
                  {/* Circle inside tab */}
                  <div className="step-circle">
                    {completedSteps.includes(step.id) ? (
                      <Image src="/assets/bx-check.svg.svg" />
                    ) : (
                      `0${step.id}`
                    )}
                  </div>
                  <div className="d-flex flex-column">
                    <p
                      className="step-label fs-four m-0 fw-normal"
                      style={{ color: "#717680" }}
                    >
                      {step.subTitle}
                    </p>
                    <p
                      className="step-label m-0 fs-four fw-medium"
                      style={{
                        color:
                          currentStep === step.id
                            ? "#5925DC"
                            : completedSteps.includes(step.id)
                            ? "#181D27"
                            : "#535862",
                      }}
                    >
                      {step.title}
                    </p>
                  </div>
                  {/* Bottom Bar for Active Step (Full Step Width) */}
                  {currentStep === step.id && (
                    <div
                      className="stepper-bottomline d-none d-lg-block"
                      style={{
                        left: step.id === 1 && "0",
                        right: step.id === steps.length && "0",
                      }}
                      ref={(el) => {
                        if (el) {
                          const stepElement = document.getElementById(
                            `step-container-${step.id}`
                          );
                          if (stepElement) {
                            const extraWidth =
                              step.id === 1 || step.id === steps.length
                                ? 24
                                : 0;
                            el.style.width = `${
                              stepElement.offsetWidth + extraWidth
                            }px`;
                          }
                        }
                      }}
                    ></div>
                  )}
                </div>
              ))}
            </div>
          )}
          {/* Content Section */}
          {currentStep === 1 && <GuidedSetupBusinessInfo />}
          {currentStep === 2 && (
            <GuidedSetupCustNoura
              show={true}
              handleClose={() => {}}
              onSave={() => {}}
            />
          )}
          {currentStep === 3 && (
            <>
              <h2 className="fw-bold fs-two">
                Experience Nouraa Like Your Customers
              </h2>
              <p className="gray-color">
                Test and refine Nouraa’s responses to ensure a seamless customer
                interaction.
              </p>
              <TestNoura showLeftNav={false} />
            </>
          )}

          {currentStep === 0 && isCompleted && (
            <div className="completion-screen">
              <img
                src="/assets/material-symbols_check-rounded.svg"
                alt="Right Icon"
                className="green-rounded-border p-2"
              />
              <h6 className="fs-three my-3">All Done</h6>
              <p>
                Perfect! You have completed the setup. Your Nouraa is in Action
              </p>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer
          className="step-footer py-3"
          style={{
            backgroundColor: "#ffffff",
            paddingRight: "20px",
            paddingLeft: "20px",
          }}
        >
          <div
            className={`buttons m-0 d-flex align-items-center w-100 ${
              currentStep > 1 && !isCompleted
                ? "justify-content-between"
                : "justify-content-end"
            }`}
            style={{ gap: "8px" }}
          >
            {/* Back button (not on step 1 and not completed) */}
            {currentStep > 1 && !isCompleted && (
              <Image
                onClick={handleBack}
                src={"/assets/arrow-left.svg"}
                style={{
                  height: "20px",
                  width: "20px",
                }}
                className="d-flex cursor-pointer"
              />
            )}

            {/* Next / Launch / Go to Dashboard Button */}
            {isCompleted ? (
              <button
                onClick={() => (window.location.href = "/overview")}
                style={{
                  width: "156px",
                }}
                className="guided-setup-button py-2 px-1 fw-bold m-0 fs-four border-0 d-flex justify-content-center align-items-center"
              >
                Go to Dashboard
                <Image
                  src={"/assets/arrow-right-white.svg"}
                  style={{
                    height: "20px",
                    width: "20px",
                  }}
                />
              </button>
            ) : currentStep < steps.length ? (
              <button
                onClick={handleNext}
                variant="primary"
                className="guided-setup-button px-3 py-2 fw-bold m-0 fs-four border-0 d-flex justify-content-center align-items-center"
              >
                Next
                <Image
                  src={"/assets/arrow-right-white.svg"}
                  style={{
                    height: "20px",
                    width: "20px",
                  }}
                />
              </button>
            ) : (
              <button
                onClick={handleLaunch}
                className="guided-setup-button px-3 py-2 fw-bold m-0 fs-four border-0 d-flex justify-content-center align-items-center gap-2"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    Launch
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                  </>
                ) : (
                  <>
                    Launch
                    <Image
                      src="/assets/arrow-right-white.svg"
                      style={{ height: "20px", width: "20px" }}
                      alt="Launch"
                    />
                  </>
                )}
              </button>
            )}
          </div>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default GuidedSetupDetails;
